import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createDomainsClient } from './api';
import type { PublisherDomainUpdatePayload } from './publisherDomainForm';
import type {
  DomainComplianceMutationPayload,
  DomainsFilter,
  PublisherDomainCreatePayload,
  PublisherDomainsFilter,
} from './model';

export const domainsQueryKey = ['supply', 'domains'] as const;
export const domainsComplianceQueryKey = [...domainsQueryKey, 'compliance'] as const;
export const domainsComplianceListsQueryKey = [...domainsComplianceQueryKey, 'list'] as const;
export const domainApprovalsQueryKey = [...domainsComplianceQueryKey, 'approvals'] as const;
export const publisherDomainsQueryKey = (publisherId: string) =>
  [...domainsQueryKey, 'publisher', publisherId] as const;

export enum DomainMutationKey {
  SetPublisherDomainActive = 'SET_PUBLISHER_DOMAIN_ACTIVE',
  UpdatePublisherDomain = 'UPDATE_PUBLISHER_DOMAIN',
  UpdateApprovals = 'UPDATE_APPROVALS',
  CreatePublisherDomain = 'CREATE_PUBLISHER_DOMAIN',
  ImportPublisherDomains = 'IMPORT_PUBLISHER_DOMAINS',
  ExportPublisherDomains = 'EXPORT_PUBLISHER_DOMAINS',
  ReimportPublisherDomains = 'REIMPORT_PUBLISHER_DOMAINS',
}

function useDomainsClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return createDomainsClient({
    config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
    accessToken: session.accessToken,
  });
}

export function useDomainsQuery(filter: DomainsFilter) {
  const client = useDomainsClient();
  return useQuery({
    queryKey: [...domainsComplianceListsQueryKey, filter],
    queryFn: () => client.list(filter),
  });
}

export function usePublisherDomainsQuery(publisherId: string, filter: PublisherDomainsFilter) {
  const client = useDomainsClient();
  return useQuery({
    queryKey: [...publisherDomainsQueryKey(publisherId), filter],
    queryFn: () => client.listPublisherDomains(publisherId, filter),
  });
}

export function usePublisherDomainStatusCountsQuery(publisherId: string, searchTerm: string) {
  const client = useDomainsClient();
  const search = searchTerm.trim();
  return useQuery({
    queryKey: [...publisherDomainsQueryKey(publisherId), { statusCounts: true }, search],
    queryFn: () => client.getPublisherStatusCounts(publisherId, search),
    enabled: Boolean(publisherId),
  });
}

export function useDomainPublishersQuery(enabled = true) {
  const client = useDomainsClient();
  return useQuery({
    queryKey: [...domainsQueryKey, 'publishers'],
    queryFn: () => client.listPublishers(),
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

export function useDomainApprovalsQuery(domain: string | undefined) {
  const client = useDomainsClient();
  return useQuery({
    queryKey: [...domainApprovalsQueryKey, domain],
    queryFn: () => client.listApprovals(domain ?? ''),
    enabled: Boolean(domain),
  });
}

export function useDomainComplianceMutations() {
  const client = useDomainsClient();
  const queryClient = useQueryClient();
  const invalidateDomainLists = () =>
    queryClient.invalidateQueries({ queryKey: domainsComplianceListsQueryKey });
  const invalidatePublisherDomains = (publisherId: string) =>
    queryClient.invalidateQueries({ queryKey: publisherDomainsQueryKey(publisherId) });
  return {
    setPublisherDomainActive: useMutation({
      mutationKey: [...domainsQueryKey, DomainMutationKey.SetPublisherDomainActive],
      mutationFn: ({
        publisherId,
        domainId,
        active,
      }: {
        publisherId: string;
        domainId: string;
        active: boolean;
      }) => client.setPublisherDomainActive(publisherId, domainId, active),
      onSuccess: (_data, variables) => {
        void invalidatePublisherDomains(variables.publisherId);
      },
    }),
    updatePublisherDomain: useMutation({
      mutationKey: [...domainsQueryKey, DomainMutationKey.UpdatePublisherDomain],
      mutationFn: ({
        publisherId,
        domainId,
        payload,
      }: {
        publisherId: string;
        domainId: string;
        payload: PublisherDomainUpdatePayload;
      }) => client.updatePublisherDomain(publisherId, domainId, payload),
      onSuccess: (_data, variables) => {
        void invalidatePublisherDomains(variables.publisherId);
      },
    }),
    updateApprovals: useMutation({
      mutationKey: [...domainsQueryKey, DomainMutationKey.UpdateApprovals],
      mutationFn: (payload: DomainComplianceMutationPayload) => client.updateApprovals(payload),
      onSuccess: () =>
        Promise.all([
          invalidateDomainLists(),
          queryClient.invalidateQueries({ queryKey: domainApprovalsQueryKey }),
        ]),
    }),
    createPublisherDomain: useMutation({
      mutationKey: [...domainsQueryKey, DomainMutationKey.CreatePublisherDomain],
      mutationFn: ({
        publisherId,
        payload,
      }: {
        publisherId: string;
        payload: PublisherDomainCreatePayload;
      }) => client.createPublisherDomain(publisherId, payload),
      onSuccess: (_data, variables) => invalidatePublisherDomains(variables.publisherId),
    }),
    importPublisherDomains: useMutation({
      mutationKey: [...domainsQueryKey, DomainMutationKey.ImportPublisherDomains],
      mutationFn: ({ publisherId, file }: { publisherId: string; file: File }) =>
        client.importPublisherDomains(publisherId, file),
      onSuccess: (_data, variables) => invalidatePublisherDomains(variables.publisherId),
    }),
    exportPublisherDomains: useMutation({
      mutationKey: [...domainsQueryKey, DomainMutationKey.ExportPublisherDomains],
      mutationFn: (publisherId: string) => client.exportPublisherDomains(publisherId),
    }),
    reimportPublisherDomains: useMutation({
      mutationKey: [...domainsQueryKey, DomainMutationKey.ReimportPublisherDomains],
      mutationFn: ({ publisherId, file }: { publisherId: string; file: File }) =>
        client.reimportPublisherDomains(publisherId, file),
      onSuccess: (_data, variables) => invalidatePublisherDomains(variables.publisherId),
    }),
  };
}
